import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { Sidebar } from '@src/components/Sidebar';
import { Appearance, Button } from '@src/components/Button';
import DescriptionField from '@src/components/DescriptionField';
import SectionTitle from '@src/components/SectionTitle';
import { Container } from '@src/components/Container';
import { FUNNEL_APPLICATIONS_ROUTE, FUNNEL_EDIT_ROUTE } from '@src/Routes/links';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { trackViewCreateTargeting, trackViewCreateApplication, trackEvent } from '@src/metrics';
import { OPENING_ROUTE } from '@src/components/Preview/appRoutes';
import {
  useGenerateOpeningCommentMutation,
  useGenerateOpeningMessageMutation,
  useSaveOpeningMutation,
} from '@src/graphql/generated';
import { ActiveTab } from '@src/components/Sidebar/enums/activeTab.enum';
import { useAppDispatch } from '@src/redux';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { ContainerType } from '@src/components/Container/enums/containerType.enum';
import { AiSuggestionButton } from '@src/components/AiSuggestionButton';
import { PositionTitle } from '../PositionTitle';
import { COMMENT_MIN_LENGTH, MESSAGE_MIN_LENGTH } from '../constants/textLength';
import { OpeningFields } from './interfaces/openingFields.interface';
import { CreateMessageProps } from './props';
import './styles.scss';

export const CreateMessage: FC<CreateMessageProps> = ({ benefits, company, isLoading, opening }) => {
  const dispatch = useAppDispatch();
  const commentAbortControllerRef = useRef(new AbortController());
  const messageAbortControllerRef = useRef(new AbortController());
  const [openingFields, setOpeningFields] = useState<OpeningFields>({
    comment: opening.comment || '',
    message: opening.message || '',
    title: opening.title || '',
  });

  const shouldEnableAiButtons = !!company?.hasPublishedProfile;

  const [saveOpening, { loading: isSaving }] = useSaveOpeningMutation({
    onError: () => dispatch(setErrorFlashMessage()),
  });

  const handleOpeningSave = useCallback(
    debounce(async openingInput => {
      await saveOpening({
        variables: {
          openingId: opening.id,
          openingInput: openingInput,
        },
      });
    }, 300),
    [],
  );

  const onFieldChange = (field: keyof OpeningFields, value: string): void => {
    if (field === 'comment') {
      commentAbortControllerRef.current.abort();
      commentAbortControllerRef.current = new AbortController();
    }

    if (field === 'message') {
      messageAbortControllerRef.current.abort();
      messageAbortControllerRef.current = new AbortController();
    }

    setOpeningFields(prevFields => ({
      ...prevFields,
      [field]: value,
    }));

    handleOpeningSave({ [field]: value });
  };

  const [generateOpeningComment, { loading: isCommentAiSuggestionLoading }] = useGenerateOpeningCommentMutation({
    context: {
      fetchOptions: {
        signal: commentAbortControllerRef.current.signal,
      },
    },
    onCompleted: ({ generateOpeningComment }) => {
      if (generateOpeningComment) {
        onFieldChange('comment', generateOpeningComment);
      } else {
        dispatch(
          setErrorFlashMessage('Generating the introductory message is failing at the moment. Please try again later.'),
        );
      }
    },
    onError: () => {
      dispatch(
        setErrorFlashMessage('Generating the introductory message is failing at the moment. Please try again later.'),
      );
    },
    variables: {
      openingId: opening.id,
    },
  });

  const [generateOpeningMessage, { loading: isMessageAiSuggestionLoading }] = useGenerateOpeningMessageMutation({
    context: {
      fetchOptions: {
        signal: messageAbortControllerRef.current.signal,
      },
    },
    onCompleted: ({ generateOpeningMessage }) => {
      if (generateOpeningMessage) {
        onFieldChange('message', generateOpeningMessage);
      } else {
        dispatch(
          setErrorFlashMessage('Generating the job role description is failing at the moment. Please try again later.'),
        );
      }
    },
    onError: () => {
      dispatch(
        setErrorFlashMessage('Generating the job role description is failing at the moment. Please try again later.'),
      );
    },
    variables: {
      openingId: opening.id,
    },
  });

  useEffect(() => {
    if (shouldEnableAiButtons && !openingFields.comment && !isCommentAiSuggestionLoading) {
      generateOpeningComment();
    }

    if (shouldEnableAiButtons && !openingFields.message && !isMessageAiSuggestionLoading) {
      generateOpeningMessage();
    }
  }, []);

  const openingId = opening.id;
  const isContinueButtonDisabled =
    !openingFields.title ||
    !openingFields.message ||
    openingFields.message.length < MESSAGE_MIN_LENGTH ||
    openingFields.comment.length < COMMENT_MIN_LENGTH ||
    isSaving ||
    isLoading;

  return (
    <div className="page create-opening funnels">
      <div className="page-layout">
        <Sidebar
          benefits={benefits}
          companyProfile={company?.profile}
          defaultTab={ActiveTab.PREVIEW}
          opening={opening}
          previewUrl={OPENING_ROUTE}
        />

        <Container className="create-message__content" type={ContainerType.BORDERED}>
          <div className="page-section">
            <PositionTitle
              initTitle={openingFields.title}
              openingId={openingId}
              setTitle={value => onFieldChange('title', value)}
            />
          </div>

          <div className="page-section">
            <SectionTitle
              subtitle="Add a personal message that will catch job seeker's attention. Think about what is your brand's unique story that makes you stand out? You can use [Markdown](https://www.markdownguide.org/cheat-sheet/) formatting."
              title="Introductory message"
            />

            <div className="create-message-field-wrapper">
              <DescriptionField
                defaultValue={openingFields.comment}
                inputClassName="create-message__field create-message__field--dense"
                minLength={COMMENT_MIN_LENGTH}
                onBlur={e =>
                  trackEvent({
                    context: MetricContext.CREATE_OPENING,
                    interaction: MetricAction.SET_MESSAGE,
                    payload: {
                      'Message': e.target.value,
                      'Opening Id': openingId,
                    },
                  })
                }
                onChange={value => onFieldChange('comment', value)}
                title={`Introductory message* (min ${COMMENT_MIN_LENGTH} characters)`}
                titleSize="S"
              />

              {shouldEnableAiButtons && (
                <AiSuggestionButton
                  className="create-message-field-ai-button"
                  isLoading={isCommentAiSuggestionLoading}
                  onRegenerateClick={() => generateOpeningComment()}
                />
              )}
            </div>
          </div>

          <div className="page-section">
            <SectionTitle
              subtitle="Add a short description for the role you're looking to fill. You can use [Markdown](https://www.markdownguide.org/cheat-sheet/) formatting."
              title="Job role description"
            />

            <div className="create-message-field-wrapper">
              <DescriptionField
                defaultValue={openingFields.message}
                inputClassName="create-message__field"
                minLength={MESSAGE_MIN_LENGTH}
                onBlur={e =>
                  trackEvent({
                    context: MetricContext.CREATE_OPENING,
                    interaction: MetricAction.SET_MESSAGE,
                    payload: {
                      'Message': e.target.value,
                      'Opening Id': openingId,
                    },
                  })
                }
                onChange={value => onFieldChange('message', value)}
                title={`Role description* (min ${MESSAGE_MIN_LENGTH} characters)`}
                titleSize="S"
              />

              {shouldEnableAiButtons && (
                <AiSuggestionButton
                  className="create-message-field-ai-button"
                  isLoading={isMessageAiSuggestionLoading}
                  onRegenerateClick={() => generateOpeningMessage()}
                />
              )}
            </div>
          </div>

          <div className="page-section">
            <Button
              className="create-opening-continue"
              isDisabled={isContinueButtonDisabled}
              isLink
              onClick={() =>
                trackViewCreateApplication({
                  'Event Source': MetricEventSource.CONTINUE_BUTTON,
                  'Opening Id': openingId,
                  'Previous Context': MetricContext.CREATE_OPENING_MESSAGE,
                })
              }
              params={{ openingId }}
              to={FUNNEL_APPLICATIONS_ROUTE}
            >
              Continue
            </Button>

            <Button
              appearance={Appearance.INVISIBLE}
              className="create-message__back-button"
              isLink
              onClick={() =>
                trackViewCreateTargeting({
                  'Event Source': MetricEventSource.BACK_BUTTON,
                  'Opening Id': openingId,
                  'Previous Context': MetricContext.CREATE_OPENING_MESSAGE,
                })
              }
              params={{ openingId }}
              to={FUNNEL_EDIT_ROUTE}
            >
              Back
            </Button>
          </div>
        </Container>
      </div>
    </div>
  );
};
